import axios from 'axios';
import { useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { useAppContext } from '@/context';

// Unified interfaces
export interface CloudinaryFile {
  url: string;
  public_id: string;
  original_filename: string;
  resource_type?: 'image' | 'video';
  [key: string]: TypeAny;
}

export interface FileWithMeta {
  file: File;
  isRaw?: boolean;
}

export interface ErrorType {
  code: 'UPLOAD_ERROR';
  error: string;
}

export interface CloudinaryUploadResult {
  cloudinary_id: string;
  filename: string;
  resource_type?: 'image' | 'video';
  is_raw: boolean;
}

export interface UploadProgress {
  progress: number;
  isUploading: boolean;
  uploadedCount: number;
  uploadedFiles: CloudinaryFile[];
}

// Configuration constants
const CHUNK_SIZE_THRESHOLD = 20 * 1024 * 1024; // 20MB threshold for chunking
const SLICE_SIZE = 10 * 1024 * 1024; // 10MB slice size

// Utility function to standardize media formats for browser compatibility
function convertToStandardFormat(url: string, format?: string, resourceType?: string): string {
  if (!url || !format) return url;

  const lowerResourceType = resourceType?.toLowerCase();

  // Always convert images to JPG for maximum browser compatibility
  if (lowerResourceType === 'image') {
    return url.replace(/\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|avif|tiff|ico)$/i, '.jpg');
  }

  // Always convert videos to MP4 for maximum browser compatibility
  if (lowerResourceType === 'video') {
    return url.replace(/\.(mp4|mov|avi|mkv|webm|flv|wmv|m4v|3gp|ogv)$/i, '.mp4');
  }

  // For other resource types, return as-is
  return url;
}

// Utility function for file slicing (cross-browser compatibility)
function slice(file: File, start: number, end: number): Blob {
  const s =
    (file as TypeAny).mozSlice ?? (file as TypeAny).webkitSlice ?? file.slice ?? (() => new Blob());
  return s.call(file, start, end);
}

// Core upload function
async function uploadSingleFile(
  file: File,
  publicId: string,
  onProgress?: (progress: number) => void
): Promise<CloudinaryFile> {
  const apiType =
    file.type === 'application/pdf' ? 'raw' : file.type.includes('video') ? 'video' : 'image';

  if (file.size > CHUNK_SIZE_THRESHOLD) {
    // Chunked upload for large files
    const XUniqueUploadId = +new Date();

    for (let start = 0; start < file.size; start += SLICE_SIZE) {
      let end = start + SLICE_SIZE;
      if (end > file.size) {
        end = file.size;
      }

      const piece = slice(file, start, end);
      const formdata = new FormData();
      formdata.append('file', piece);
      formdata.append('upload_preset', process.env.NEXT_PUBLIC_CLOUDINARY_PRESET || '');
      formdata.append('public_id', publicId);

      // eslint-disable-next-line no-await-in-loop
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.NEXT_PUBLIC_CLOUDINARY_DOMAIN}/${apiType}/upload`,
        formdata,
        {
          headers: {
            'X-Unique-Upload-Id': XUniqueUploadId,
            'Content-Range': `bytes ${start}-${end - 1}/${file.size}`
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const chunkProgress = (progressEvent.loaded / progressEvent.total) * 100;
              const overallProgress =
                ((start + chunkProgress * SLICE_SIZE) / 100 / file.size) * 100;
              onProgress(Math.min(overallProgress, 100));
            }
          }
        }
      );

      if (response?.data?.done) {
        const result = {
          ...response.data,
          original_filename: file.name,
          resource_type: apiType === 'raw' ? 'image' : apiType
        };

        // Standardize formats for browser compatibility
        if (result.url && result.format) {
          result.url = convertToStandardFormat(result.url, result.format, result.resource_type);
        }
        if (result.secure_url && result.format) {
          result.secure_url = convertToStandardFormat(
            result.secure_url,
            result.format,
            result.resource_type
          );
        }

        return result;
      }
    }

    throw new Error('Chunked upload failed');
  } else {
    // Direct upload for smaller files
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', process.env.NEXT_PUBLIC_CLOUDINARY_PRESET || '');
    data.append('public_id', publicId);

    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.NEXT_PUBLIC_CLOUDINARY_DOMAIN}/${apiType}/upload`,
      data,
      {
        timeout: 120000,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            onProgress(progress);
          }
        }
      }
    );

    const result = {
      ...response.data,
      original_filename: file.name,
      resource_type: apiType === 'raw' ? 'image' : apiType
    };

    // Standardize formats for browser compatibility
    if (result.url && result.format) {
      result.url = convertToStandardFormat(result.url, result.format, result.resource_type);
    }
    if (result.secure_url && result.format) {
      result.secure_url = convertToStandardFormat(
        result.secure_url,
        result.format,
        result.resource_type
      );
    }

    return result;
  }
}

// Hook for multiple file uploads with progress tracking
export function useUnifiedCloudinaryUpload() {
  const { setAlert } = useAppContext();
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<CloudinaryFile[]>([]);
  const [uploadedCount, setUploadedCount] = useState(0);

  const uploadFiles = useCallback(
    async (files: FileWithMeta[], onSuccess?: (files: CloudinaryFile[]) => void) => {
      const fileCount = files.length;
      setUploadedCount(0);
      setIsUploading(true);
      setUploadedFiles([]);
      setProgress(0);

      let results: CloudinaryFile[] = [];

      try {
        for (let i = 0; i < fileCount; i++) {
          const { file, isRaw } = files[i];
          const publicId = uuid();

          // eslint-disable-next-line no-await-in-loop
          const result = await uploadSingleFile(file, publicId, (fileProgress) => {
            const overallProgress = ((i + fileProgress / 100) / fileCount) * 100;
            setProgress(overallProgress);
          });

          const fileResult = { ...result, isRaw };
          results.push(fileResult);
          setUploadedCount((prev) => prev + 1);
          setUploadedFiles((prev) => [...prev, fileResult]);
        }

        if (onSuccess) {
          onSuccess(results);
        }
      } catch (error) {
        console.error('Upload error:', error);
        setAlert('Upload failed. Please try again.');
        results = [];
      } finally {
        setIsUploading(false);
      }

      return results;
    },
    [setAlert]
  );

  return {
    progress,
    isUploading,
    uploadedFiles,
    uploadedCount,
    uploadFiles
  };
}

// Hook for single file upload (replaces useCloudinaryUpload)
export function useSingleCloudinaryUpload() {
  const { setAlert } = useAppContext();
  const [result, setResult] = useState<CloudinaryFile | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const uploadFile = useCallback(
    async (file: File | string, publicId: string) => {
      setIsLoading(true);
      setResult(null);

      try {
        let fileToUpload: File;

        // Handle both File objects and string (base64/data URL)
        if (typeof file === 'string') {
          // Convert string to File object
          const response = await fetch(file);
          const blob = await response.blob();
          fileToUpload = new File([blob], 'upload', { type: blob.type });
        } else {
          fileToUpload = file;
        }

        const uploadResult = await uploadSingleFile(fileToUpload, publicId);
        setResult(uploadResult);
        return uploadResult;
      } catch (error) {
        console.error('Upload error:', error);
        setAlert('Upload failed. Please try again.');
        setResult(null);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [setAlert]
  );

  return { result, isLoading, uploadFile };
}

// Utility function for direct uploads (replaces cloudinaryUpload)
export async function uploadToCloudinary(
  files: File[],
  baseId?: string,
  options?: {
    rawMedia?: boolean;
    references?: boolean;
  }
): Promise<CloudinaryUploadResult[]> {
  const { rawMedia = false } = options || {};
  const results: CloudinaryUploadResult[] = [];

  files.forEach(async (file) => {
    const publicId = baseId ? `${baseId}-${uuid()}` : uuid();

    try {
      const result = await uploadSingleFile(file, publicId);

      results.push({
        cloudinary_id: result.public_id,
        filename: result.original_filename,
        resource_type: result.resource_type,
        is_raw: rawMedia
      });
    } catch (error) {
      console.error('Upload error:', error);
    }
  });

  return results;
}
